import type { CustomPasswordInputProps } from '@components/PasswordInputWithStrengthMeter';
import PasswordInputWithStrengthMeter from '@components/PasswordInputWithStrengthMeter';
import Tiptap from '@components/Tiptap';
import buildClassName from '@utils/build-class-name';
import type { FormRule, InputProps, SelectProps, SwitchProps } from 'antd';
import { Form, Input, InputNumber, Select, Switch } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import PhoneInput from 'antd-phone-input';
import type { PhoneNumber } from 'antd-phone-input/types';
import type { CSSProperties, FC, HTMLAttributes, MouseEvent, ReactNode } from 'react';
import { checkValidity, getFormattedNumber, parsePhoneNumber } from 'react-phone-hooks';

import styles from './EditableCell.module.scss';

export interface EditableCellProps extends HTMLAttributes<HTMLTableCellElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  placeholder?: string;
  inputType: 'text' | 'password' | 'number' | 'select' | 'switch' | 'textarea' | 'phone-number' | 'rte';
  required?: boolean;
  disabled?: boolean;
  max?: number;
  min?: number;
  autoComplete?: string;
  customRules?: FormRule[];
  selectOptions?: DefaultOptionType[];
  customPasswordInputProps?: CustomPasswordInputProps;
  children: ReactNode;
  inputProps?: InputProps;
  selectProps?: SelectProps;
  switchProps?: SwitchProps;
  inputStyle?: CSSProperties;
}

const EditableCell: FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  placeholder,
  inputType,
  required = true,
  disabled = false,
  max,
  min,
  autoComplete,
  customRules,
  selectOptions,
  customPasswordInputProps,
  children,
  inputProps,
  selectProps,
  switchProps,
  inputStyle,
  className,
  ...restProps
}) => {
  const onClickHandler = (event: MouseEvent<HTMLElement>) => event.stopPropagation();

  const inputNode =
    inputType === 'number' ? (
      <InputNumber
        onClick={onClickHandler}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={inputStyle}
      />
    ) : inputType === 'select' ? (
      <Select
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          (option?.label && typeof option.label === 'string' ? option.label : '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        options={selectOptions}
        onClick={onClickHandler}
        disabled={disabled}
        placeholder={placeholder}
        style={inputStyle}
        {...selectProps}
      />
    ) : inputType === 'switch' ? (
      <Switch
        {...switchProps}
        onClick={(checked, event) => {
          event.stopPropagation();
          switchProps?.onClick?.(checked, event);
        }}
        disabled={switchProps?.disabled ?? disabled}
        style={inputStyle}
      />
    ) : inputType === 'textarea' ? (
      <Input.TextArea
        rows={4}
        disabled={disabled}
        count={{ show: !!max, max: max }}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={inputStyle}
      />
    ) : inputType === 'phone-number' ? (
      <PhoneInput
        enableSearch
        disabled={disabled}
        count={{ show: !!max, max: max }}
        placeholder={placeholder}
        country="us"
        allowClear={true}
        autoComplete={autoComplete}
        style={inputStyle}
      />
    ) : inputType === 'rte' ? (
      <Tiptap />
    ) : (
      <Input
        onClick={onClickHandler}
        disabled={disabled}
        count={{ show: !!max, max: max }}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={inputStyle}
        {...inputProps}
      />
    );

  const phoneValidator: FormRule = () => ({
    validator: (_, number: string | null) => {
      if (number === null) {
        return Promise.resolve();
      }
      const phoneNumber = parsePhoneNumber(getFormattedNumber(number.replace(/^\+/, '')));
      return (phoneNumber.countryCode === null && phoneNumber.areaCode === null && phoneNumber.phoneNumber === null) ||
        checkValidity(phoneNumber)
        ? Promise.resolve()
        : Promise.reject('Invalid phone number format.');
    },
  });

  const formItemRules: FormRule[] = [
    ...(required
      ? [{ required: true, message: `Please enter a ${title ? title.toLowerCase() : 'value'}.` }]
      : [{ required: false }]),
    ...(max !== undefined ? [{ max: max, message: `${title} can not exceed ${max} characters.` }] : []),
    ...(min !== undefined ? [{ min: min, message: `${title} can not be less than ${min} characters.` }] : []),
    ...(inputType === 'phone-number' ? [phoneValidator] : []),
    ...(customRules && customRules.length > 0 ? customRules : []),
  ];

  return (
    <td className={buildClassName(className, inputType === 'rte' && styles.rteCell)} {...restProps}>
      {editing ? (
        inputType === 'password' ? (
          <PasswordInputWithStrengthMeter
            name={dataIndex}
            rules={formItemRules}
            style={{ margin: 0 }}
            inputProps={{
              ...(customPasswordInputProps ?? {}),
              onClick: onClickHandler,
              disabled: disabled,
              count: { show: !!max, max: max },
              placeholder: placeholder,
            }}
          />
        ) : (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={formItemRules}
            valuePropName={inputType === 'switch' ? 'checked' : undefined}
            normalize={(value, prevValue): unknown => {
              switch (inputType) {
                case 'phone-number':
                  return inputType === 'phone-number'
                    ? value.countryCode !== null
                      ? `+${(value as PhoneNumber).countryCode || ''}${(value as PhoneNumber).areaCode || ''}${(value as PhoneNumber).phoneNumber || ''}`
                      : null
                    : value;
                case 'select':
                  return prevValue !== undefined && prevValue !== null && value === undefined ? null : value;
                default:
                  return value;
              }
            }}
          >
            {inputNode}
          </Form.Item>
        )
      ) : (
        children
      )}
    </td>
  );
};

export default EditableCell;
